import "@fontsource/inter";
import { lazy } from "react";
import { Theme } from "./Theme";
import styled from "@emotion/styled";

const Box = lazy(() => import("@mui/material/Box"));
const Tab = lazy(() => import("@mui/material/Tab"));
const Link = lazy(() => import("@mui/material/Link"));
const Card = lazy(() => import("@mui/material/Card"));
const Stack = lazy(() => import("@mui/material/Stack"));
const Button = lazy(() => import("@mui/material/Button"));
const Typography = lazy(() => import("@mui/material/Typography"));

export const SmallTitle = styled(Typography)({
  color: Theme.palette.muted,
  textTransform: "uppercase",
  letterSpacing: "1.4px",
  fontFamily: "DM Sans, sans-serif",
  fontWeight: 700,
});

export const FormButton = styled(Button)((props) => ({
  width: "100%",
  padding: props.mobile ? "5px" : props.small ? "10px" : "15px",
  border: props.alt ? "1px solid" : "none",
  borderColor: props.alt && "rgba(249,115,22,0.3)",
  borderRadius: "12px",
  textWrap: "nowrap",
  fontFamily: "Oswald, sans-serif",
  fontWeight: 700,
  fontSize: props.mobile ? "0.6rem" : props.small ? "0.8rem" : "1rem",
  letterSpacing: ".1em",
  color: props.alt ? Theme.palette.orange : "#fff",
  background: props.alt
    ? "rgba(249,115,22,0.08)"
    : "linear-gradient(135deg, #f97316, #ea580c)",
  transition: "all .22s ease",

  "&:hover": {
    transform: "translateY(-2px)",
    background: props.alt && "rgba(249,115,22,0.08)",
  },
  "&:active": { transform: "translateY(0)" },
  "&:disabled": {
    background: "#1e1e1e",
    color: "#444",
    boxShadow: "none",
    cursor: "not-allowed",
    transform: "none",
  },
}));

export const MainText = styled(Typography)((props) => ({
  fontFamily: "DM Sans,  Segoe UI Emoji, Noto Color Emoji, sans-serif",
  color: Theme.palette.text,
}));

export const MainButton = styled(Button)({
  width: "90%",
  background: Theme.palette.orange,
  borderRadius: "10px",
  padding: "8px",
  border: 2,
  borderColor: Theme.palette.orange,
  transition: "400ms ease-in-out",

  "&:hover": {
    background: Theme.palette.card,
    border: 2,
    borderColor: Theme.palette.border,
  },
});
export const FormContainer = styled(Stack)((props) => ({
  background: "#111",
  borderRadius: 22,
  border: "1px solid #1e1e1e",
  overflow: "hidden",
  boxShadow: "0 8px 40px rgba(0,0,0,.5)",
  width: "65%",
  minWidth: "300px",
}));

export const MainTitle = styled(Typography)((props) => ({
  fontFamily: "Oswald, Segoe UI Emoji, Noto Color Emoji, sans-serif",
  fontWeight: 700,
  color: Theme.palette.text,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
}));

export const SecondaryTitle = styled(Typography)((props) => ({
  fontFamily: "Oswald, sans-serif",
  fontWeight: 700,
  fontSize: "0.7rem",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: Theme.palette.muted,
}));

export const StyledLink = styled(Link)((props) => ({
  color: props.inactive ? "#444" : "#f97316",
  fontFamily: "DM Sans, sans-serif",
  textDecoration: "none",
  fontWeight: 700,
  transition: "opacity .15s",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.75,
  },
}));

export const MenuLink = styled(Link)({
  textDecoration: "none",
});

export const scrollBar = {
  "::-webkit-scrollbar": {
    width: "5px",
    height: "5px",
    backgroundColor: "rgba(162,162,162, 0.15)",
    borderRadius: "14px",
  },
  "::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(118, 192, 174, 0.25)",
    borderRadius: "14px",
  },
  "::-webkit-scrollbar-track-piece:start": {
    background: "transparent",
  },
  "::-webkit-scrollbar-track-piece:end": {
    background: "transparent",
  },

  ":hover": {
    "::-webkit-scrollbar": {
      backgroundColor: "rgba(162,162,162, 0.5)",
    },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: Theme.palette.orange,
    },
  },
};

export const ConversionTableScrollBar = {
  height: "50vh",
  "::-webkit-scrollbar": {
    width: "5px",
    height: "5px",
    backgroundColor: "rgba(162,162,162, 0.15)",
    borderRadius: "14px",
  },
  "::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(118, 192, 174, 0.25)",
    borderRadius: "14px",
  },
  "::-webkit-scrollbar-track-piece:start": {
    background: "transparent",
  },
  "::-webkit-scrollbar-track-piece:end": {
    background: "transparent",
  },

  ":hover": {
    "::-webkit-scrollbar": {
      backgroundColor: "rgba(162,162,162, 0.5)",
    },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: Theme.palette.orange,
    },
  },
};

export const BasicButton = styled(Button)((props) => ({
  color: props.dark ? "#19031F" : Theme.palette.orange,
  width: props.mobile ? "20px" : props.smallscreen ? "30px" : "35px",
  minWidth: props.mobile ? "20px" : props.smallscreen ? "30px" : "35px",
  height: props.mobile ? "20px" : props.smallscreen ? "30px" : "35px",
  borderRadius: "50%",
  transition: "500ms ease",
  "&:hover": {
    backgroundColor: props.dark ? "#19031F" : Theme.palette.orange,
    color: props.dark ? Theme.palette.orange : "#19031F",
  },
}));

export const CommonTab = styled(Tab)((props) => ({
  color: "#FFEDDD",
  fontFamily: "Inter",
  fontSize: props.mobile ? "0.7rem" : "0.9rem",
  "&.Mui-selected": {
    color: Theme.palette.orange,
  },
}));

export const ProfileWodCard = styled(Stack)((props) => ({
  borderBottom: "2px solid #f97316",
  minHeight: "150px",
  paddingLeft: "5px",
  gap: props.mobile ? "6px" : "12px",
  justifyContent: "space-between",
  paddingBottom: "5px",
  cursor: "pointer",
}));

export const SpacedRow = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
});

export const ExerciseProgress = styled(Stack)((props) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  borderBottom: "1px solid",
  borderBottomColor: Theme.palette.border,
  borderLeft: "2px solid",
  borderLeftColor: props.borderLeftColor,
  padding: "10px 5px",
  cursor: "pointer",
}));

export const CenteredRow = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
});

export const AllCenteredRow = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
});

export const AllCenteredColumn = styled(Stack)({
  justifyContent: "center",
  alignItems: "center",
});

export const LeaderBoardText = styled(Typography)({
  fontFamily: "Inter",
  fontWeight: "bold",
  color: "#3E3C3C",
});

export const ModalTitle = styled(Typography)((props) => ({
  fontSize: props.big ? "2rem" : props.mobile ? "1.5rem" : "1.5rem",
  fontFamily: "Inter",
  color: "#E7E8E6",
}));

export const CommonTitle = styled(Typography)((props) => ({
  fontFamily: "Inter",
  color: props.dark ? "#3E3C3C" : "#FFEDDD",
  fontSize: props.mobile ? "0.9rem" : props.smallscreen ? "1rem" : "1.2rem",
}));

export const ErrorText = styled(Typography)({
  fontFamily: "Inter",
  fontSize: "0.8rem",
  color: "#F77371",
});

export const WorkoutBox = styled(Box)((props) => ({
  background: Theme.palette.surface,
  border: "1px solid",
  borderColor: Theme.palette.border,
  borderRadius: "12px",
  // padding: "10px",
  height: "95%",

  "&:hover": {
    background: props.hover && "#183042",
  },
}));

export const MenuStack = styled(Stack)((props) => ({
  justifyContent: "space-evenly",
  zIndex: 2,
  overflow: "hidden",
  height: props.mobile ? "100%" : "70%",
  alignItems: "center",
  width: "70%",
  minHeight: "500px",
  borderRight: `1px solid ${Theme.palette.border}`,
  minWidth: props.mobile ? "50px" : "68px",
  borderRadius: props.mobile ? "0px" : "10px",
  background: Theme.palette.surface,
}));

export const ModalButton = styled(Button)((props) => ({
  border: "1px solid  #f97316",
  color: "#f97316",
  fontSize: props.mobile ? "0.7rem" : "1rem",
  "&:hover": {
    backgroundColor: "#f97316",
    color: "#3E3C3C",
  },
}));

export const PersonalBestStack = styled(Stack)((props) => ({
  padding: "10px",
  width: props.mobile ? "100%" : "30%",
  height: props.mobile ? "25vh" : "30vh",
  borderRadius: 16,
  background: Theme.palette.card,
  border: `1px solid ${Theme.palette.border}`,
  minWidth: "250px",
  minHeight: "200px",
}));

export const DashBoardCard = styled(Card)((props) => ({
  background: "#1a1a1a",
  padding: props.mobile ? "4px" : "8px",
  width: "95%",
  height: "100%",
}));

export const ChartBox = styled(Box)((props) => ({
  padding: props.mobile ? "5px" : "10px",
  borderRadius: "6px",
  background: "rgba(56, 59, 57, 0.4)",
  textAlign: "right",
}));
